import React from "react";
import styled from "styled-components";
import { device } from "../../helpers/style";

const LEFT_TOP = "left-top";
const LEFT_BOTTOM = "left-bottom";
const RIGHT_TOP = "right-top";
const RIGHT_BOTTOM = "right-bottom";
type ANCHOR_POSITION =
    | "left-top"
    | "left-bottom"
    | "right-top"
    | "right-bottom";

const OFFSET = 7;

const Container = styled.div`
    position: relative;
`;
const InnerContainer = styled.div`
    z-index: 100;
    user-select: none;
    position: absolute;
    border-radius: 3px;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    color: #fff;
    background-color: #000;
    white-space: nowrap;

    & > div {
        flex: 1;
    }

    @media ${device.phone} {
        display: none;
    }
`;

const Contents = styled.div`
    display: flex;
    flex-direction: row;
    height: 31px;
    line-height: 31px;
`;

const RTL = styled.div`
    display: flex;
    font-size: 18px;
    line-height: 26px;
`;

const LTR = styled.div`
    display: flex;
    padding: 0 8px;
    font-size: 18px;
    font-family: "Source Code Pro", monospace;
    font-weight: 500;
    background-color: #4d8cf5;
    color: #fff;

    &:empty {
        display: none;
    }
`;

interface LeftProps {
    right: number;
    top: number;
    hasLTR: boolean;
}
const Left = styled(InnerContainer)`
    right: ${(p: LeftProps) => p.right}px;
    top: ${(p: LeftProps) => p.top}px;
    padding: ${(p: LeftProps) => (p.hasLTR ? "0 10px 0 0" : "0 10px 0 10px")};

    &:after {
        position: absolute;
        content: " ";
        width: 0;
        height: 0;
    }

    & ${Contents} {
        flex-direction: row;
    }

    & ${LTR} {
        margin: 0 10px 0 0;
    }
`;

const LeftTop = styled(Left)`
    border-bottom-right-radius: 0;

    &:after {
        right: -${OFFSET}px;
        bottom: 0;
        border-bottom: solid ${OFFSET}px #000;
        border-right: solid ${OFFSET}px transparent;
    }
`;

const LeftBottom = styled(Left)`
    border-top-right-radius: 0;

    &:after {
        right: -${OFFSET}px;
        top: 0;
        border-top: solid ${OFFSET}px #000;
        border-right: solid ${OFFSET}px transparent;
    }
`;

interface RightProps {
    left: number;
    top: number;
    hasLTR: boolean;
}

const Right = styled(InnerContainer)`
    left: ${(p: RightProps) => p.left}px;
    top: ${(p: RightProps) => p.top}px;

    &:after {
        position: absolute;
        content: " ";
        width: 0;
        height: 0;
    }

    & ${Contents} {
        flex-direction: row-reverse;
    }

    & ${LTR} {
        margin: 0 0 0 10px;
    }

    & ${RTL} {
        padding-left: 10px;
    }
`;

const RightTop = styled(Right)`
    border-bottom-left-radius: 0;

    &:after {
        left: -${OFFSET}px;
        bottom: 0;
        border-bottom: solid ${OFFSET}px #000;
        border-left: solid ${OFFSET}px transparent;
    }
`;

const RightBottom = styled(Right)`
    border-top-left-radius: 0;

    &:after {
        left: -${OFFSET}px;
        top: 0;
        border-top: solid ${OFFSET}px #000;
        border-left: solid ${OFFSET}px transparent;
    }
`;

interface TipProps {
    text: string;
    anchor: ANCHOR_POSITION;
    ltr?: string;
}
interface TipState {
    show: boolean;
}
export class Tip extends React.Component<TipProps, TipState> {
    private containerRef: React.RefObject<HTMLElement>;

    constructor(props: TipProps) {
        super(props);

        this.state = {
            show: false,
        };

        this.containerRef = React.createRef<HTMLDivElement>();
    }

    onMouseOver = () => this.setState({ show: true });
    onMouseOut = () => this.setState({ show: false });

    contents() {
        const { text, ltr } = this.props;
        return (
            <Contents>
                <RTL>{text}</RTL>
                <LTR>{ltr}</LTR>
            </Contents>
        );
    }

    view() {
        if (!this.state.show) {
            return null;
        }

        const r = this.containerRef;
        if (!r || !r.current) {
            return null;
        }

        const width = r.current.offsetWidth;
        const height = r.current.offsetHeight;
        const { anchor, ltr } = this.props;
        const hasLTR = !!ltr;

        if (anchor === LEFT_TOP) {
            return (
                <LeftTop hasLTR={hasLTR} right={width + OFFSET} top={-OFFSET}>
                    {this.contents()}
                </LeftTop>
            );
        } else if (anchor === LEFT_BOTTOM) {
            return (
                <LeftBottom
                    hasLTR={hasLTR}
                    right={width + OFFSET}
                    top={height * 0.7}
                >
                    {this.contents()}
                </LeftBottom>
            );
        } else if (anchor === RIGHT_TOP) {
            return (
                <RightTop hasLTR={hasLTR} left={width + OFFSET} top={-OFFSET}>
                    {this.contents()}
                </RightTop>
            );
        } else if (anchor === RIGHT_BOTTOM) {
            return (
                <RightBottom
                    hasLTR={hasLTR}
                    left={width + OFFSET}
                    top={height * 0.7}
                >
                    {this.contents()}
                </RightBottom>
            );
        }

        return null;
    }

    render() {
        return (
            <Container
                ref={this.containerRef as React.RefObject<HTMLDivElement>}
                onMouseOver={this.onMouseOver}
                onMouseOut={this.onMouseOut}
            >
                {this.props.children}
                {this.view()}
            </Container>
        );
    }
}
